import { isEqual } from 'lodash';
import React from 'react';
import { JsonDiffComponent } from 'json-diff-react';

interface JsonDiffViewerProps {
  current: any;
  stored: any;
  title?: string;
  maxHeight?: string;
}

const DIFF_STYLES = {
  additionClassName:
    'bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs font-mono',
  deletionClassName:
    'bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-xs font-mono',
  unchangedClassName: 'text-gray-700 text-xs font-mono',
  frameClassName: 'text-xs leading-relaxed whitespace-pre-wrap font-mono'
};

const DIFF_OPTIONS = {
  verbose: true,
  keepUnchangedValues: true,
  full: true
};

/**
 * Reusable component for displaying JSON diffs between two objects.
 * Shows a structured comparison with visual indicators for additions, deletions, and unchanged values.
 */
export const JsonDiffViewer: React.FC<JsonDiffViewerProps> = ({
  current,
  stored,
  title = 'Snapshot Comparison',
  maxHeight = 'max-h-96'
}) => {
  return (
    <div className={`${maxHeight} overflow-auto`}>
      <div className="sticky top-0 bg-gray-50 p-2 border-b border-border-default rounded-t-md">
        <h4 className="font-semibold text-sm">{title}</h4>
        <div className="flex gap-4 text-xs mt-1">
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 border border-green-300"></div>
            Current
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-100 border border-red-300"></div>
            Stored
          </span>
        </div>
      </div>
      <div className="p-2">
        {isEqual(current, stored) ? (
          <div>
            <div className="mb-2 text-xs text-green-700 bg-green-100 rounded p-2">
              No differences detected. Showing current state:
            </div>
            <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">
              {JSON.stringify(current, null, 2)}
            </pre>
          </div>
        ) : (
          <JsonDiffComponent
            jsonA={stored}
            jsonB={current}
            styleCustomization={DIFF_STYLES}
            jsonDiffOptions={DIFF_OPTIONS}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Specialized component for displaying snapshot diffs with visitor management context
 */
export const SnapshotDiffViewer: React.FC<{
  current: any;
  stored: any;
}> = ({ current, stored }) => {
  return (
    <div>
      <h3 className="font-semibold text-sm mb-2">Snapshot Drift Detected</h3>
      <p className="text-xs text-gray-600 mb-3">
        Current booking data differs from what the vendor knows:
      </p>
      <JsonDiffViewer current={current} stored={stored} title="What Changed" />
    </div>
  );
};
